'use client';

import { useParams, usePathname, useSearchParams } from 'next/navigation';

import {
  DEFAULT_LOCALE,
  TLanguagesConfigItem,
  TLocale,
  locales as baseLocales,
  languagesConfigByLocale,
} from '@northladder/utilities';

interface IHrefOptions {
  /**
   * The account ID of the user got from the /[accountId] route param.
   * This is used in redirection and must be cross-checked with index value
   * within `activeSession.index`
   */
  accountId: string;
  /**
   * The active language currently displayed by the UI.
   * This contains the locale, id, flag icon and label.
   */
  activeLanguage: TLanguagesConfigItem;
  /**
   * This renders the home path with the query param intact.
   */
  homePath: string;
  /**
   * The base URL `*without*` the locale part
   */
  href: string;
  /**
   * The locale as fetched from the route path.
   */
  locale: TLocale;
}

/**
 * A simple hook to remove the locale part from the pathname.
 * Since nextjs router removes the asPath prop from links, and the locale prop
 * is so undependable, we need to remove the locale from the pathname to generate
 * a new one with the target locale.
 */
export function useHrefOptions(): IHrefOptions {
  const { locale = DEFAULT_LOCALE, accountId = '' } =
    useParams<{
      locale: TLocale;
      accountId: string;
    }>() || {};
  const activeLanguage = languagesConfigByLocale[locale];

  const pathname = usePathname() || '';
  const parts = pathname.split('/');
  const localePart = parts[1] as TLocale;
  // Join the parts back together to form the new pathname
  const path = parts.slice(2).join('/') || '/';

  const searchParams = useSearchParams();
  const queryString = searchParams?.toString();

  // Check if the first part of the pathname is a locale
  if (baseLocales.includes(localePart)) {
    // Remove the locale part
    parts.splice(1, 1);
  }
  const href = `/${path}${queryString ? `?${queryString}` : ''}`;
  const homePath = queryString ? `/?${queryString}` : '/';

  return {
    accountId,
    activeLanguage,
    homePath,
    href,
    locale,
  };
}
